import { Controller } from "@hotwired/stimulus"
import { syncCrispWithOpenOverlays } from "../utils/hide_crisp_on_modals"

// Palette ⌘K / Ctrl+K : ouvre la recherche globale depuis n’importe quelle page.
export default class extends Controller {
  static targets = ["backdrop", "dialog"]
  static values = { open: Boolean }

  connect() {
    this.boundShortcut = this.handleShortcut.bind(this)
    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundResultChosen = this.handleResultChosen.bind(this)
    this.boundBeforeCache = this.handleBeforeCache.bind(this)
    document.addEventListener("keydown", this.boundShortcut)
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    this.element.addEventListener("turbo:click", this.boundResultChosen)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundShortcut)
    document.removeEventListener("keydown", this.boundKeydown)
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    this.element.removeEventListener("turbo:click", this.boundResultChosen)
    window.clearTimeout(this.hideTimeout)
    this.unlockBody()
  }

  openValueChanged() {
    if (this.openValue) {
      this.show()
    } else {
      this.hide()
    }
  }

  open(event) {
    event?.preventDefault?.()
    this.openValue = true
  }

  close(event) {
    event?.preventDefault?.()
    this.openValue = false
  }

  toggle() {
    this.openValue = !this.openValue
  }

  handleShortcut(event) {
    if (event.key !== "k" && event.key !== "K") return
    if (event.altKey || event.shiftKey) return

    const apple = this.isApplePlatform()
    if (apple && !event.metaKey) return
    if (!apple && !event.ctrlKey) return
    if (apple && event.ctrlKey) return
    if (!apple && event.metaKey) return

    // Une modale Bootstrap a priorité (formulaire, etc.)
    if (document.querySelector(".modal.show")) return

    event.preventDefault()

    // Dashboard / show : focus la barre déjà visible plutôt qu’une 2e palette.
    const inlineInput = this.findVisibleInlineGlobalSearchInput()
    if (inlineInput) {
      if (this.openValue) this.close()
      inlineInput.focus({ preventScroll: true })
      inlineInput.select()
      return
    }

    this.toggle()
  }

  findVisibleInlineGlobalSearchInput() {
    const forms = document.querySelectorAll('form[data-search-global="true"]')
    for (const form of forms) {
      if (form.closest(".global-search-palette__dialog")) continue

      const input = form.querySelector('[data-search-target="input"]')
      if (!input) continue

      const style = window.getComputedStyle(input)
      if (style.display === "none" || style.visibility === "hidden") continue

      const rect = input.getBoundingClientRect()
      if (rect.width < 2 || rect.height < 2) continue

      return input
    }
    return null
  }

  handleKeydown(event) {
    if (!this.openValue) return
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    }
  }

  // Un résultat choisi (clic, Entrée, « Voir tout ») part en visite pleine page : la palette se
  // referme tout de suite, dans la même image que le squelette de la page d'arrivée (cf.
  // nav_instant_skeleton.ts). Sinon elle restait ouverte par-dessus jusqu'au remplacement du
  // <body>, soit toute la durée de la requête : l'app semblait ne réagir qu'à moitié.
  //
  // `turbo:click` plutôt que `click` : Turbo ne l'émet que pour les liens qu'il prend en charge,
  // pas pour un Cmd+clic qui ouvre un onglet — la palette reste alors ouverte, on peut en ouvrir
  // d'autres. Et seulement pour un lien qui sort de la frame des résultats : un lien chargé dans
  // une frame ne quitte pas la page.
  handleResultChosen(event) {
    if (!this.openValue) return
    if (event.target.closest?.("a")?.dataset.turboFrame !== "_top") return

    this.close()
  }

  // Turbo met en cache la page qu'on quitte au moment où la réponse arrive. Si c'est avant la fin
  // du fondu, la palette y resterait figée, corps verrouillé — et un retour arrière la
  // rouvrirait. On termine donc la fermeture sans attendre.
  handleBeforeCache() {
    if (!this.hasDialogTarget || this.dialogTarget.hidden) return

    this.openValue = false
    this.hide({ immediate: true })
  }

  show() {
    if (!this.hasBackdropTarget || !this.hasDialogTarget) return

    this.backdropTarget.hidden = false
    this.dialogTarget.hidden = false
    document.body.classList.add("global-search-palette-open")
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", this.boundKeydown)
    syncCrispWithOpenOverlays()

    requestAnimationFrame(() => {
      this.backdropTarget.classList.add("is-visible")
      this.dialogTarget.classList.add("is-visible")
      this.focusInput()
    })
  }

  hide({ immediate = false } = {}) {
    if (!this.hasBackdropTarget || !this.hasDialogTarget) return

    this.backdropTarget.classList.remove("is-visible")
    this.dialogTarget.classList.remove("is-visible")
    document.removeEventListener("keydown", this.boundKeydown)

    window.clearTimeout(this.hideTimeout)
    if (immediate) {
      this.finishHide()
    } else {
      this.hideTimeout = window.setTimeout(() => this.finishHide(), 160)
    }
  }

  finishHide() {
    if (this.openValue) return
    this.backdropTarget.hidden = true
    this.dialogTarget.hidden = true
    this.unlockBody()
    this.clearPaletteInput()
  }

  unlockBody() {
    document.body.classList.remove("global-search-palette-open")
    document.body.style.removeProperty("overflow")
    syncCrispWithOpenOverlays()
  }

  focusInput() {
    const input = this.dialogTarget.querySelector('[data-search-target="input"]')
    if (!input) return
    input.focus({ preventScroll: true })
    input.select()
  }

  clearPaletteInput() {
    const input = this.dialogTarget.querySelector('[data-search-target="input"]')
    if (input) input.value = ""
    const frame = this.dialogTarget.querySelector("turbo-frame")
    if (frame) frame.innerHTML = ""
  }

  isApplePlatform() {
    const platform = navigator.userAgentData?.platform || navigator.platform || ""
    return /Mac|iPhone|iPad|iPod/i.test(platform)
  }
}
